import { useMemo, useState } from 'react';
import { Eraser, Filter, RefreshCw, RotateCcw, Search } from 'lucide-react';
import type {
  InternalRouterClientIdentityAccount,
  InternalRouterClientIdentityDevice,
  InternalRouterClientIdentitySession,
} from '@nadoutong/contracts';
import {
  useResetRouterClientIdentitySession,
  useRouterClientIdentityAccount,
  useRouterClientIdentityAccounts,
} from '../api/useRouterClientIdentities.ts';
import type { RouterClientIdentityFilters } from '../api/routerClientIdentities.ts';
import { CustomerRef } from '../components/identity/CustomerRef.tsx';
import { StatusChip } from '../components/StatusChip.tsx';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { formatLocalTimestamp } from '../components/formatters.ts';
import {
  Button,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Panel,
  TBody,
  Table,
  TableSkeleton,
  Td,
  Th,
  THead,
  Tr,
} from '../ui/index.ts';

interface FilterDraft {
  readonly orgId: string;
  readonly accountId: string;
  readonly memberAccountId: string;
  readonly search: string;
}

type DetailDevice = InternalRouterClientIdentityDevice & {
  readonly sessions: readonly InternalRouterClientIdentitySession[];
};

const EMPTY_FILTERS: FilterDraft = {
  orgId: '',
  accountId: '',
  memberAccountId: '',
  search: '',
};

export function RouterClientIdentitiesPage(): JSX.Element {
  const toast = useToast();
  const [draft, setDraft] = useState<FilterDraft>(EMPTY_FILTERS);
  const [filters, setFilters] = useState<RouterClientIdentityFilters>({ limit: 50 });
  const accounts = useRouterClientIdentityAccounts(filters);
  const rows = accounts.data?.accounts ?? [];
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const detail = useRouterClientIdentityAccount(selectedAccountId);
  const resetSession = useResetRouterClientIdentitySession();

  const selectedAccount = useMemo(() => {
    if (selectedAccountId === null) return rows[0] ?? null;
    return rows.find((account) => account.id === selectedAccountId) ?? null;
  }, [rows, selectedAccountId]);

  const totals = useMemo(() => ({
    accounts: rows.length,
    activeSessions: rows.reduce((sum, account) => sum + account.activeSessionCount, 0),
    recentRequests: rows.reduce((sum, account) => sum + account.recentRequestCount, 0),
  }), [rows]);

  function updateDraft<K extends keyof FilterDraft>(key: K, value: FilterDraft[K]): void {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  function applyFilters(): void {
    setFilters({
      orgId: draft.orgId,
      accountId: draft.accountId,
      memberAccountId: draft.memberAccountId,
      search: draft.search,
      limit: 50,
    });
  }

  function clearFilters(): void {
    setDraft(EMPTY_FILTERS);
    setFilters({ limit: 50 });
  }

  function handleResetSession(sessionId: string): void {
    resetSession.mutate(sessionId, {
      onSuccess: () => toast.success('Session reset queued'),
      onError: (error) => toast.error(coerceErrorMessage(error)),
    });
  }

  return (
    <section className="flex flex-col gap-6" data-testid="router-client-identities-page">
      <div className="flex flex-wrap items-center gap-2">
        <StatusChip variant="neutral" label="Gateway SQL" />
        <StatusChip variant="ok" label={`${totals.accounts} accounts`} />
        <StatusChip variant={totals.activeSessions > 0 ? 'ok' : 'neutral'} label={`${totals.activeSessions} active sessions`} />
        <StatusChip variant={totals.recentRequests > 0 ? 'warning' : 'neutral'} label={`${totals.recentRequests} recent requests`} />
      </div>
      <PageHeader
        title="Router Client Identities"
        sub="Router-owned account, device, and session identities projected into Claude Code, Anthropic, and OpenAI egress."
        actions={
          <Button type="button" onClick={() => void accounts.refetch()}>
            <RefreshCw aria-hidden="true" className="h-4 w-4" />
            Refresh
          </Button>
        }
      />

      <form
        className="grid gap-4 rounded-lg border border-line-soft bg-card p-4 lg:grid-cols-[repeat(4,minmax(0,1fr))_auto_auto]"
        onSubmit={(event) => {
          event.preventDefault();
          applyFilters();
        }}
      >
        <FilterInput
          label="Org id"
          fieldId="rci-filter-org-id"
          value={draft.orgId}
          onChange={(value) => updateDraft('orgId', value)}
          mono
        />
        <FilterInput
          label="Account id"
          fieldId="rci-filter-account-id"
          value={draft.accountId}
          onChange={(value) => updateDraft('accountId', value)}
          mono
        />
        <FilterInput
          label="Member account id"
          fieldId="rci-filter-member-account-id"
          value={draft.memberAccountId}
          onChange={(value) => updateDraft('memberAccountId', value)}
          mono
        />
        <FilterInput
          label="Search"
          fieldId="rci-filter-search"
          value={draft.search}
          onChange={(value) => updateDraft('search', value)}
          mono
        />
        <Button type="submit" variant="primary" className="self-end">
          <Filter aria-hidden="true" className="h-4 w-4" />
          Apply filters
        </Button>
        <Button type="button" onClick={clearFilters} className="self-end">
          <Eraser aria-hidden="true" className="h-4 w-4" />
          Clear
        </Button>
      </form>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.25fr)_minmax(380px,0.75fr)]">
        <AccountsTable
          rows={rows}
          loading={accounts.isLoading}
          selectedAccountId={selectedAccount?.id ?? null}
          onSelect={setSelectedAccountId}
        />
        <DetailPanel
          account={detail.data?.account ?? selectedAccount}
          devices={detail.data?.devices ?? []}
          loading={detail.isFetching && selectedAccountId !== null}
          resetPendingId={resetSession.variables ?? null}
          resetPending={resetSession.isPending}
          onResetSession={handleResetSession}
        />
      </div>
    </section>
  );
}

function FilterInput({
  label,
  fieldId,
  value,
  onChange,
  mono = false,
}: {
  readonly label: string;
  readonly fieldId: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly mono?: boolean;
}): JSX.Element {
  return (
    <FormField label={label} fieldId={fieldId} className="min-w-0">
      <Input
        id={fieldId}
        aria-label={label}
        mono={mono}
        value={value}
        onChange={(event) => onChange(event.currentTarget.value)}
      />
    </FormField>
  );
}

function AccountsTable({
  rows,
  loading,
  selectedAccountId,
  onSelect,
}: {
  readonly rows: readonly InternalRouterClientIdentityAccount[];
  readonly loading: boolean;
  readonly selectedAccountId: string | null;
  readonly onSelect: (id: string) => void;
}): JSX.Element {
  return (
    <Panel caption="Accounts" count={rows.length} padded className="min-h-[560px]">
      <div className="flex flex-col gap-4">
        <p className="m-0 text-dense text-paper-low">
          Stable router account UUIDs scoped by org and member account.
        </p>
        {loading ? (
          <TableSkeleton rows={7} rowHeight={56} testId="router-client-accounts-loading" />
        ) : rows.length === 0 ? (
          <div className="rounded-lg bg-well">
            <EmptyState title="No router client identities" />
          </div>
        ) : (
          <Table className="border-0">
            <THead>
              <Tr interactive={false}>
                <Th>Member</Th>
                <Th>Account UUID</Th>
                <Th num>Sessions</Th>
                <Th num>Requests</Th>
                <Th>Last seen</Th>
                <Th className="text-right">Action</Th>
              </Tr>
            </THead>
            <TBody>
              {rows.map((row) => (
                <Tr key={row.id} className={row.id === selectedAccountId ? 'bg-active' : undefined}>
                  <Td className="align-top">
                    <div className="flex flex-col gap-1">
                      <span className="font-mono text-dense font-semibold text-paper">{row.memberAccountId}</span>
                      <span className="font-mono text-micro text-paper-low">
                        <CustomerRef orgId={row.orgId} /> / <CustomerRef accountId={row.accountId} />
                      </span>
                    </div>
                  </Td>
                  <Td mono className="align-top">
                    {row.accountUuid}
                  </Td>
                  <Td num className="align-top">
                    <div className="flex flex-col gap-1">
                      <span className="text-paper">{row.activeSessionCount} active</span>
                      <span className="text-paper-low">{row.sessionCount} total</span>
                    </div>
                  </Td>
                  <Td num emphasis className="align-top">
                    {row.recentRequestCount}
                  </Td>
                  <Td className="align-top">{formatLocalTimestamp(row.lastSeenAt)}</Td>
                  <Td className="text-right align-top">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label={`Open ${row.id}`}
                      onClick={() => onSelect(row.id)}
                    >
                      <Search aria-hidden="true" className="h-3.5 w-3.5" />
                      Open
                    </Button>
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        )}
      </div>
    </Panel>
  );
}

function DetailPanel({
  account,
  devices,
  loading,
  resetPendingId,
  resetPending,
  onResetSession,
}: {
  readonly account: InternalRouterClientIdentityAccount | null;
  readonly devices: readonly DetailDevice[];
  readonly loading: boolean;
  readonly resetPendingId: string | null;
  readonly resetPending: boolean;
  readonly onResetSession: (id: string) => void;
}): JSX.Element {
  if (account === null) {
    return (
      <aside className="flex min-h-[560px] items-center justify-center rounded-lg border border-line-soft bg-card p-8">
        <EmptyState title="Select an account" />
      </aside>
    );
  }

  return (
    <Panel
      caption="Identity detail"
      padded
      className="min-h-[560px]"
      testId="router-client-detail"
      actions={
        <StatusChip variant={account.enabled ? 'ok' : 'error'} label={account.enabled ? 'enabled' : 'disabled'} />
      }
    >
      <div className="flex flex-col gap-5">
        <p className="m-0 break-all font-mono text-dense text-paper-low">{account.id}</p>

        <div className="grid grid-cols-2 gap-3 rounded-lg bg-well p-4">
          <Metric label="Devices" value={String(account.deviceCount)} />
          <Metric label="Active sessions" value={String(account.activeSessionCount)} />
          <Metric label="Recent requests" value={String(account.recentRequestCount)} />
          <Metric label="Last seen" value={formatLocalTimestamp(account.lastSeenAt)} />
        </div>

        {loading ? (
          <TableSkeleton rows={4} rowHeight={52} testId="router-client-detail-loading" />
        ) : devices.length === 0 ? (
          <div className="rounded-lg bg-well p-6 text-body text-paper-low">No devices recorded</div>
        ) : (
          <div className="flex flex-col gap-4">
            {devices.map((device) => (
              <section key={device.id} className="rounded-lg bg-well p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-dense font-semibold text-paper">{device.id}</span>
                      <StatusChip variant={device.source === 'generated' ? 'neutral' : 'ok'} label={device.source} />
                    </div>
                    <p className="mt-2 break-all font-mono text-micro text-paper-low">{device.deviceId}</p>
                  </div>
                  <span className="font-mono text-dense text-paper-mid">{device.recentRequestCount} req</span>
                </div>
                <div className="mt-4 flex flex-col gap-2">
                  {device.sessions.map((session) => (
                    <div
                      key={session.id}
                      className="grid gap-3 rounded-lg border border-line-soft bg-card px-3 py-3 text-dense text-paper md:grid-cols-[minmax(0,1fr)_auto]"
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-mono font-semibold">{session.id}</span>
                          <StatusChip
                            variant={session.revokedAt === null ? 'ok' : 'error'}
                            label={session.revokedAt === null ? 'active' : 'revoked'}
                          />
                          <StatusChip variant="neutral" label={session.source} />
                        </div>
                        <div className="mt-2 grid gap-1 text-paper-low">
                          <span className="break-all font-mono">{session.sessionId}</span>
                          <span>Last seen {formatLocalTimestamp(session.lastSeenAt)} · {session.recentRequestCount} recent requests</span>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="danger"
                        size="sm"
                        className="self-start"
                        aria-label={`Reset ${session.id}`}
                        disabled={session.revokedAt !== null || (resetPending && resetPendingId === session.id)}
                        onClick={() => onResetSession(session.id)}
                      >
                        <RotateCcw aria-hidden="true" className="h-3.5 w-3.5" />
                        Reset
                      </Button>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </Panel>
  );
}

function Metric({ label, value }: { readonly label: string; readonly value: string }): JSX.Element {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="text-label text-paper-low">{label}</span>
      <span className="truncate font-mono text-dense font-semibold text-paper">{value}</span>
    </div>
  );
}
